/**
 * PurchaseCreditsModal — "Buy more" flow for AI credits.
 *
 * Fetches available packs from `/api/me/ai-credits/packs` and POSTs the
 * user's pick to `/api/me/ai-credits/purchase`. The endpoint is
 * currently MOCKED — it grants credits directly without charging a
 * card so admins can preview the flow end-to-end before Stripe lands.
 * The mocked:true flag from the backend surfaces a small "test mode"
 * badge so nobody thinks they're being charged.
 */
import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../App';
import { Button } from './ui/button';
import { Sparkles, X, Check } from 'lucide-react';

export default function PurchaseCreditsModal({ open, onClose, onPurchased }) {
  const { API } = useContext(AuthContext);
  const [packs, setPacks] = useState([]);
  const [mocked, setMocked] = useState(false);
  const [buying, setBuying] = useState(null);

  useEffect(() => {
    if (!open) return;
    let cancel = false;
    axios.get(`${API}/me/ai-credits/packs`).then(({ data }) => {
      if (cancel) return;
      setPacks(data.packs || []);
      setMocked(!!data.mocked);
    }).catch(() => {});
    return () => { cancel = true; };
  }, [open, API]);

  if (!open) return null;

  const buy = async (pack) => {
    try {
      setBuying(pack.id);
      const { data } = await axios.post(`${API}/me/ai-credits/purchase`, {
        pack_id: pack.id,
        origin_url: window.location.origin,
      });
      if (data?.url) {
        // Stripe Checkout hosted page — payment happens off-site and
        // credits are granted from our webhook / status-poll after the
        // user is redirected back to /checkout/success.
        window.location.href = data.url;
        return;
      }
      // Fallback: legacy mocked path where the endpoint grants inline.
      toast.success(`${pack.credits.toLocaleString()} credits added`);
      onPurchased && onPurchased(data);
      onClose && onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Purchase failed');
    } finally { setBuying(null); }
  };

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-navy-950/70 backdrop-blur-sm px-4"
      data-testid="purchase-credits-modal"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-2xl bg-card border border-border shadow-2xl p-5 max-h-[90dvh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-2 mb-3">
          <div className="h-9 w-9 rounded-xl bg-amber-500 text-navy flex items-center justify-center">
            <Sparkles className="w-4 h-4" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-widest font-bold text-amber-700">AI credits</div>
            <h3 className="font-heading text-lg font-extrabold text-foreground">Buy more credits</h3>
            {mocked && (
              <div className="text-[10px] text-amber-700 dark:text-amber-300 font-bold mt-0.5">
                Test mode — no card charged
              </div>
            )}
            {!mocked && (
              <div className="text-[10px] text-muted-foreground mt-0.5">
                Secure checkout on Stripe · you'll be redirected
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="h-7 w-7 rounded-full hover:bg-muted flex items-center justify-center"
            aria-label="Close"
            data-testid="purchase-credits-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <ul className="space-y-2">
          {packs.map((p, idx) => {
            const recommended = idx === 1; // middle pack is the visual hero
            const onSale = !!p.discount_active && p.sale_price_usd < p.original_price_usd;
            return (
              <li
                key={p.id}
                className={`rounded-xl border p-3 flex items-center gap-3 ${onSale ? 'border-rose-500 bg-rose-500/5' : recommended ? 'border-amber-500 bg-amber-500/5' : 'border-border'}`}
                data-testid={`credit-pack-${p.id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="font-heading text-base font-extrabold">{p.label}</span>
                    {onSale && (
                      <span className="text-[9px] uppercase tracking-widest font-black bg-rose-500 text-white rounded-full px-1.5 py-0.5" data-testid={`credit-pack-sale-badge-${p.id}`}>
                        {p.percent_off}% off
                      </span>
                    )}
                    {!onSale && recommended && (
                      <span className="text-[9px] uppercase tracking-widest font-black bg-amber-500 text-navy rounded-full px-1.5 py-0.5">
                        Popular
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {p.credits.toLocaleString()} credits ·{' '}
                    {onSale ? (
                      <>
                        <span className="line-through mr-1" data-testid={`credit-pack-original-${p.id}`}>${p.original_price_usd}</span>
                        <span className="font-bold text-rose-600 dark:text-rose-400" data-testid={`credit-pack-sale-${p.id}`}>${p.sale_price_usd}</span>
                      </>
                    ) : (
                      <>${p.price_usd}</>
                    )}
                  </div>
                </div>
                <Button
                  size="sm"
                  onClick={() => buy(p)}
                  disabled={buying === p.id}
                  className="rounded-full font-bold bg-primary text-primary-foreground hover:bg-primary/90"
                  data-testid={`credit-pack-buy-${p.id}`}
                >
                  {buying === p.id ? 'Adding…' : (<><Check className="w-3 h-3 mr-1" /> Buy</>)}
                </Button>
              </li>
            );
          })}
        </ul>

        <p className="mt-3 text-[10px] text-muted-foreground italic">
          Bonus credits added to your monthly balance and reset with the next billing cycle.
        </p>
      </div>
    </div>
  );
}
